import { PerformanceReviewEditReviewersModal } from "@/pages/talent/performance-reviews/view/widgets/PerformanceReviewEditReviewersModal.tsx";
import { PerformanceReviewReopenModal } from "@/pages/talent/performance-reviews/view/widgets/PerformanceReviewReopenModal.tsx";
import { PerformanceReviewSelectReviewTypeModal } from "@/pages/talent/performance-reviews/view/widgets/PerformanceReviewSelectReviewTypeModal.tsx";
import {
  usePerformanceReviewComplete,
  usePerformanceReviewDelete,
  usePerformanceReviewLaunch,
  usePerformanceReviewPause,
} from "@/queries/performance-reviews/use-performance-reviews.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import {
  ApiPerformanceReview,
  ApiPerformanceReviewReviewerType,
  ApiPerformanceReviewStatus,
  getApiPerformanceReviewStatusTitle,
} from "@/types/performance-review.types.ts";
import { ConfirmationModal, Tag, TagVariants, useModals } from "@skailer-com/uikit";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

interface ReviewActionParams {
  id: number;
  status: ApiPerformanceReviewStatus;
}

export const usePerformanceReviewsHook = () => {
  const { openModal } = useModals();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const deleteReview = usePerformanceReviewDelete();
  const launchReview = usePerformanceReviewLaunch();
  const pauseReview = usePerformanceReviewPause();
  const completeReview = usePerformanceReviewComplete();

  const openSelectPeers = ({
    performanceReviewId,
    employeeId,
    reviewerTypes,
    withStatuses = true,
  }: {
    performanceReviewId: number;
    employeeId: number;
    reviewerTypes: ApiPerformanceReviewReviewerType[];
    withStatuses?: boolean;
  }) => {
    if (reviewerTypes.length === 1) {
      openModal(PerformanceReviewEditReviewersModal, {
        performanceReviewId,
        employeeId,
        reviewerTypeId: reviewerTypes[0].id,
        withStatuses,
      });
    } else {
      openModal(PerformanceReviewSelectReviewTypeModal, {
        performanceReviewId,
        employeeId,
        reviewerTypes,
        withStatuses,
      });
    }
  };

  const getPerformanceReviewStatusTag = (entity: ApiPerformanceReviewStatus) => {
    let tagVariant: TagVariants = "positive";
    switch (entity) {
      case ApiPerformanceReviewStatus.draft:
        tagVariant = "secondary";
        break;
      case ApiPerformanceReviewStatus.running:
        tagVariant = "positive";
        break;
      case ApiPerformanceReviewStatus.paused:
        tagVariant = "warning";
        break;
      case ApiPerformanceReviewStatus.completed:
        tagVariant = "primary";
        break;
      case ApiPerformanceReviewStatus.pending_launch:
        tagVariant = "positive";
        break;
    }

    return <Tag variant={tagVariant}>{getApiPerformanceReviewStatusTitle(entity)}</Tag>;
  };

  const handleDeleteReview = (review: ReviewActionParams, onSuccess?: () => void) => {
    openModal(ConfirmationModal, {
      question: t("common:delete"),
      text: t("common:delete_confirm"),
      acceptTitle: t("common:delete"),
      onAccept: async function () {
        try {
          await deleteReview.mutateAsync({ id: review.id });
          analyticsService.trackEvent(analyticEvents.performanceReviews.deleted, {
            [analyticProperties.id]: review.id,
          });
          onSuccess?.();
          navigate("/performance-reviews");
        } catch {
          return;
        }
      },
    });
  };

  const handleReopenReview = (review: ApiPerformanceReview) => {
    openModal(PerformanceReviewReopenModal, {
      review,
      onSuccess: () => {
        analyticsService.trackEvent(analyticEvents.performanceReviews.reopened, {
          [analyticProperties.id]: review.id,
        });
      },
    });
  };

  const handleLaunchReview = (review: ReviewActionParams) => {
    openModal(ConfirmationModal, {
      question: t("performance_review:launch_review"),
      text: t("performance_review:launch_review_confirm"),
      acceptTitle: t("performance_review:launch_review"),
      onAccept: async function () {
        try {
          await launchReview.mutateAsync({ id: review.id });
          analyticsService.trackEvent(analyticEvents.performanceReviews.launched, {
            [analyticProperties.id]: review.id,
          });
        } catch {
          return;
        }
      },
    });
  };

  const handlePauseReview = async (review: ReviewActionParams) => {
    try {
      await pauseReview.mutateAsync({ id: review.id });
      analyticsService.trackEvent(analyticEvents.performanceReviews.paused, {
        [analyticProperties.id]: review.id,
      });
    } catch {}
  };

  const handleCompleteReview = (review: ReviewActionParams) => {
    openModal(ConfirmationModal, {
      question: t("performance_review:complete_review_question"),
      text: `${t("performance_review:complete_review_confirm")}.`,
      acceptTitle: t("performance_review:complete_review"),
      onAccept: async function () {
        try {
          await completeReview.mutateAsync({ id: review.id });
          analyticsService.trackEvent(analyticEvents.performanceReviews.completed, {
            [analyticProperties.id]: review.id,
          });
        } catch {
          return;
        }
      },
    });
  };

  const canLaunchReview = (status: ApiPerformanceReviewStatus) =>
    [ApiPerformanceReviewStatus.draft, ApiPerformanceReviewStatus.paused].includes(status);

  const canPauseReview = (status: ApiPerformanceReviewStatus) =>
    [ApiPerformanceReviewStatus.running, ApiPerformanceReviewStatus.pending_launch].includes(
      status
    );

  const canCompleteReview = (status: ApiPerformanceReviewStatus) =>
    [
      ApiPerformanceReviewStatus.pending_launch,
      ApiPerformanceReviewStatus.running,
      ApiPerformanceReviewStatus.paused,
    ].includes(status);

  const canReopenReview = (status: ApiPerformanceReviewStatus) =>
    [ApiPerformanceReviewStatus.completed].includes(status);

  return {
    openSelectPeers,
    getPerformanceReviewStatusTag,
    handleDeleteReview,
    handleReopenReview,
    handleLaunchReview,
    handlePauseReview,
    handleCompleteReview,
    canLaunchReview,
    canPauseReview,
    canCompleteReview,
    canReopenReview,
    isLaunchPending: launchReview.isPending,
    isPausePending: pauseReview.isPending,
  };
};
